import {
	closestCorners,
	DndContext,
	type DragEndEvent,
	DragOverlay,
	type DragStartEvent,
	PointerSensor,
	useDroppable,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	arrayMove,
	SortableContext,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { type ReactNode, useRef, useState } from 'react';

/**
 * Two-container sortable drag-and-drop ("transfer list") built on a single
 * dnd-kit context, so items can be reordered within the "selected" list AND
 * dragged between the "selected" and "available" lists — both with dnd-kit's
 * live row-shift animation. Drag is initiated only from a `[data-drag-handle]`
 * element (so buttons / inputs inside a row stay clickable), via DragHandle
 * from ./DraggableList.
 *
 * Usage: wrap the two list sections in <DualSortableProvider>, and render each
 * list's rows through a <SortableZone container="selected" | "available">.
 */

export type DualContainer = 'selected' | 'available';

// Only start a drag when the pointer goes down on a [data-drag-handle] element.
class HandlePointerSensor extends PointerSensor {
	public static override activators = [
		{
			eventName: 'onPointerDown' as const,
			handler: ({ nativeEvent: event }: { nativeEvent: Event }): boolean => {
				const target = event.target as HTMLElement;
				return target.closest('[data-drag-handle]') != null;
			},
		},
	];
}

interface DualSortableProviderProps<T> {
	/** The selected (ordered) items — the source of truth for reorder. */
	selected: Array<T>;
	keyExtractor: (item: T) => string;
	/**
	 * Ids of the available-pool items (membership only; their full objects are
	 * rendered by the available <SortableZone>). Lets the available list be a
	 * different item type than `selected`.
	 */
	availableIds: Array<string>;
	/** Reorder within the selected list. */
	onReorder: (next: Array<T>) => void;
	/** Move an available item into selected at the given index. */
	onAdd: (id: string, index: number) => void;
	/** Move a selected item out to available. */
	onRemove: (id: string) => void;
	disabled?: boolean;
	/** Render the floating drag preview for the active item id. */
	renderOverlay?: (activeId: string) => ReactNode;
	children: ReactNode;
}

export function DualSortableProvider<T>({
	selected,
	keyExtractor,
	availableIds,
	onReorder,
	onAdd,
	onRemove,
	disabled = false,
	renderOverlay,
	children,
}: DualSortableProviderProps<T>): React.ReactElement {
	const [activeId, setActiveId] = useState<string | null>(null);
	const rootRef = useRef<HTMLDivElement>(null);

	const sensors = useSensors(
		useSensor(HandlePointerSensor, { activationConstraint: { distance: 5 } }),
	);

	const containerOf = (id: string): DualContainer | null => {
		if (id === 'container:selected') {
			return 'selected';
		}
		if (id === 'container:available') {
			return 'available';
		}
		if (selected.some((item) => keyExtractor(item) === id)) {
			return 'selected';
		}
		if (availableIds.includes(id)) {
			return 'available';
		}
		return null;
	};

	// Which container the dragged item ended up over — by hit-testing its final
	// rect against the two container DOM rects. dnd-kit's `over` is computed from
	// collision detection that biases toward the dragged item's source container,
	// so it can't reliably tell us when an item crossed INTO the other list; the
	// geometry can.
	const containerAtY = (y: number): DualContainer | null => {
		const root = rootRef.current;
		if (root == null) {
			return null;
		}
		const zones: Array<DualContainer> = ['selected', 'available'];
		let nearest: DualContainer | null = null;
		let nearestDist = Infinity;
		for (const zone of zones) {
			const node = root.querySelector<HTMLElement>(
				`[data-dual-container="${zone}"]`,
			);
			if (node == null) {
				continue;
			}
			const rect = node.getBoundingClientRect();
			if (y >= rect.top && y <= rect.bottom) {
				return zone;
			}
			const dist = y < rect.top ? rect.top - y : y - rect.bottom;
			if (dist < nearestDist) {
				nearestDist = dist;
				nearest = zone;
			}
		}
		return nearest;
	};

	const handleDragEnd = (event: DragEndEvent): void => {
		setActiveId(null);
		const { active, over } = event;
		if (disabled) {
			return;
		}
		const activeKey = String(active.id);
		const from = containerOf(activeKey);
		if (from == null) {
			return;
		}

		// Target container by geometry; fall back to the over droppable's container.
		const translated = active.rect.current.translated;
		const to =
			translated != null
				? containerAtY(translated.top + translated.height / 2)
				: over != null
					? containerOf(String(over.id))
					: null;
		if (to == null) {
			return;
		}

		// Index within selected, derived from the over item when it's a selected row.
		const overKey = over != null ? String(over.id) : '';
		const overIndex = selected.findIndex((i) => keyExtractor(i) === overKey);

		if (from === 'selected' && to === 'selected') {
			const oldIndex = selected.findIndex((i) => keyExtractor(i) === activeKey);
			if (oldIndex !== -1 && overIndex !== -1 && oldIndex !== overIndex) {
				onReorder(arrayMove(selected, oldIndex, overIndex));
			}
		} else if (from === 'available' && to === 'selected') {
			onAdd(activeKey, overIndex === -1 ? selected.length : overIndex);
		} else if (from === 'selected' && to === 'available') {
			onRemove(activeKey);
		}
		// available -> available is a no-op (available order is derived).
	};

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCorners}
			onDragStart={(event: DragStartEvent) => {
				setActiveId(String(event.active.id));
			}}
			onDragEnd={handleDragEnd}
			onDragCancel={() => {
				setActiveId(null);
			}}
		>
			<div ref={rootRef} className="contents">
				{children}
			</div>
			<DragOverlay>
				{activeId != null && renderOverlay != null
					? renderOverlay(activeId)
					: null}
			</DragOverlay>
		</DndContext>
	);
}

interface SortableZoneProps<T> {
	container: DualContainer;
	items: Array<T>;
	keyExtractor: (item: T) => string;
	renderItem: (item: T, isDragging: boolean) => ReactNode;
	/** Rendered when the list is empty (also acts as the drop target). */
	emptyContent?: ReactNode;
	className?: string;
}

export function SortableZone<T>({
	container,
	items,
	keyExtractor,
	renderItem,
	emptyContent,
	className = '',
}: SortableZoneProps<T>): React.ReactElement {
	const ids = items.map(keyExtractor);
	const { setNodeRef } = useDroppable({ id: `container:${container}` });

	return (
		<SortableContext items={ids} strategy={verticalListSortingStrategy}>
			<div
				ref={setNodeRef}
				data-dual-container={container}
				className={className}
			>
				{items.length === 0
					? emptyContent
					: items.map((item) => (
							<SortableRow
								key={keyExtractor(item)}
								id={keyExtractor(item)}
								render={(isDragging) => renderItem(item, isDragging)}
							/>
						))}
			</div>
		</SortableContext>
	);
}

interface SortableRowProps {
	id: string;
	render: (isDragging: boolean) => ReactNode;
}

function SortableRow({ id, render }: SortableRowProps): React.ReactElement {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.4 : 1,
	};

	return (
		<div ref={setNodeRef} style={style} {...attributes} {...listeners}>
			{render(isDragging)}
		</div>
	);
}
